import { localStorageKeys } from "@/app/config/localStorageKeys";
import { PartnerType } from "@/app/utils/partners.enum";
import {
  JOURNEY_FIELD,
  JOURNEY_HEADER,
  JOURNEY_PRODUCT_HEADER,
  journeyStore,
} from "../journey";
import { httpClient } from "./httpClient";

export type SseStepState = "queued" | "running" | "done" | "failed";

export type SseStepScope = "crefisa" | "api";

export interface SseStep {
  scope: SseStepScope;
  key: string;
  label?: string;
  state: SseStepState;
  seq: number;
  ms?: number;
  message?: string;
}

export interface SseRequestError {
  statusCode: number;
  message: string;
  mensagem?: string;
  errors?: unknown;
}

export interface StreamRequestOptions {
  method: "GET" | "POST" | "PUT";
  path: string;
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
  onStep?: (step: SseStep) => void;
  journey?: PartnerType;
}

export type StreamOptions = Pick<
  StreamRequestOptions,
  "onStep" | "signal" | "journey"
>;

interface SseFrame {
  type: string;
  data: string;
}

const CONNECTION_LOST: SseRequestError = {
  statusCode: 502,
  message: "A conexão foi encerrada antes da resposta chegar. Tente novamente.",
};

function buildUrl(
  path: string,
  params?: StreamRequestOptions["params"],
): string {
  const base = (httpClient.defaults.baseURL ?? "").replace(/\/+$/, "");
  const url = new URL(
    `${base}/${path.replace(/^\/+/, "")}`,
    window.location.origin,
  );

  Object.entries(params ?? {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.set(key, String(value));
    }
  });

  return url.toString();
}

function parseFrame(raw: string): SseFrame | null {
  let type = "message";
  const dataLines: string[] = [];

  for (const line of raw.split("\n")) {
    if (!line || line.startsWith(":")) continue;

    const separator = line.indexOf(":");
    const field = separator === -1 ? line : line.slice(0, separator);
    let value = separator === -1 ? "" : line.slice(separator + 1);
    if (value.startsWith(" ")) value = value.slice(1);

    if (field === "event") type = value;
    else if (field === "data") dataLines.push(value);
  }

  if (!dataLines.length && type === "message") return null;

  return { type, data: dataLines.join("\n") };
}

function safeParse(raw: string) {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export async function streamRequest<T>({
  method,
  path,
  body,
  params,
  signal,
  onStep,
  journey,
}: StreamRequestOptions): Promise<T> {
  const accessToken = localStorage.getItem(localStorageKeys.ACCESS_TOKEN);
  const isFormData = body instanceof FormData;
  const journeyId = journeyStore.get(journey);

  const response = await fetch(buildUrl(path, params), {
    method,
    signal,
    headers: {
      Accept: "text/event-stream",
      "X-Origin": "WEB",
      "X-Platform": "web",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(journey
        ? journeyId
          ? { [JOURNEY_HEADER]: journeyId }
          : { [JOURNEY_PRODUCT_HEADER]: journey }
        : {}),
      ...(body !== undefined && !isFormData
        ? { "Content-Type": "application/json" }
        : {}),
    },
    body:
      body === undefined
        ? undefined
        : isFormData
          ? (body as FormData)
          : JSON.stringify(body),
  });

  const isStream = response.headers
    .get("content-type")
    ?.includes("text/event-stream");

  if (!isStream) {
    const payload = await response.json().catch(() => null);
    throw (
      payload ?? {
        statusCode: response.status,
        message: response.statusText || CONNECTION_LOST.message,
      }
    );
  }

  if (!response.body) throw CONNECTION_LOST;

  const reader = response.body.getReader();
  const decoder = new TextDecoder();

  let buffer = "";
  let settled = false;
  let result: T | undefined;
  let failure: SseRequestError | null = null;

  const consume = (raw: string) => {
    const frame = parseFrame(raw);
    if (!frame) return;

    if (frame.type === "step") {
      const step = safeParse(frame.data);
      if (step) onStep?.(step);
      return;
    }

    if (frame.type === "result") {
      result = safeParse(frame.data);
      settled = true;

      if (journey) {
        const id = (result as Record<string, unknown> | null)?.[JOURNEY_FIELD];
        if (typeof id === "string") journeyStore.set(journey, id);
      }

      return;
    }

    if (frame.type === "error") {
      const parsed = safeParse(frame.data);
      failure = parsed ?? {
        statusCode: 502,
        message: frame.data?.trim() || CONNECTION_LOST.message,
      };
      settled = true;
    }
  };

  const drain = () => {
    buffer = buffer.replace(/\r\n/g, "\n");

    let boundary = buffer.indexOf("\n\n");
    while (boundary !== -1) {
      consume(buffer.slice(0, boundary));
      buffer = buffer.slice(boundary + 2);
      boundary = buffer.indexOf("\n\n");
    }
  };

  for (;;) {
    const { done, value } = await reader.read();

    if (done) {
      buffer += decoder.decode();
      drain();
      if (!settled && buffer.trim()) consume(buffer);
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    drain();

    if (settled) break;
  }

  await reader.cancel().catch(() => undefined);

  if (failure) throw failure;
  if (!settled) throw CONNECTION_LOST;

  return result as T;
}
